/**
 * MyOrders Component
 *
 * Description: This component displays a list of orders with functionality for searching, filtering, and pagination. It interacts with an API to fetch orders based on query parameters and displays them in a table format. Each order item can be viewed in detail through a link.
 *
 * File Name: MyOrders.tsx
 * Date: 15-08-24
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CustomerTableWrapper from "src/Components/Customer/CustomerTableWrapper";
import PageHeader from "src/Components/Customer/PageHeader";
import SearchFilterInput from "src/Components/GlobalComponents/SearchFilterInput";
import httpRequest from "src/helpers/httpRequest";
import { IOrderItems, IqueryParams } from "src/types/GlobalInterfaces";
import { IPaginated } from "src/types/paginated";
import qs from "qs";
import { decodedToken, displayStatus, getLastSegment } from "src/helpers/helperFunctions";
import moment from "moment";
import eyeIcon from "../../assets/images/eye-icon-green.png";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import { CURRENCY } from "src/lib/constans";

interface iMyOrdersResponse {
  documents: IOrderItems[] | [];
  paginated: IPaginated;
}

type tabTypes = "Test" | "Pharmacy";
interface IParams extends IqueryParams {
  status: tabTypes;
}
interface TabItem {
  name: tabTypes;
  count: number; // Add a count property
}

const MyOrders: React.FC = () => {
  const orderStatus: any = [
    { label: "All", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Completed", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Delivered", value: "delivered" },
  ];
  const { allIndicators } = useSelector((store: RootState) => store?.redDots);

  const [tabList, setTabList] = useState<TabItem[]>([
    {
      name: "Test",
      count:
        allIndicators?.customerOrder > 0 ? allIndicators?.customerOrder : 0,
    },
    {
      name: "Pharmacy",
      count:
        allIndicators?.customerPharmacyOrder > 0
          ? allIndicators?.customerPharmacyOrder
          : 0,
    },
  ]);
  
  const handleTabClick = (tabName: tabTypes) => {
    setAllOrders([]);
    setFilterstatus("");
    setQueryParams((prev) => ({
      ...prev,
      search: "",
      status: tabName,
      page: 1,
    }));
  };
  const [filterStatus, setFilterstatus] = useState<any>("");
  const [isLoader, setIsLoader] = useState<boolean>(false); // Loader state for API calls
  const [allOrders, setAllOrders] = useState<IOrderItems[]>([]); // List of orders
  const [paginated, setPaginated] = useState<IPaginated>({
    currentPage: 1,
    totalPages: 1,
  }); // Handles pagination state
  const [queryParams, setQueryParams] = useState<IParams>({
    page: 1,
    limit: 10,
    status: "Test",
    search: "",
  }); // Query parameters for API request

  // Handles page change in pagination
  const handlePageChange = (e: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page: e,
    }));
  };

  // Handles filter change for search input
  const handleFilterChange = (val: string) => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: val,
    }));
  };

  const returnURL = (id: string): string => {
    return queryParams?.status === "Test"
      ? `/order/${id}/View`
      : `/pharmacy/order/${id}/view`;
  };

  // API_CALL: Fetches orders from the server based on query parameters
  const fetchMyOrders = async () => {
    const userId = decodedToken()?.id;
    console.log(decodedToken(), "decodedToken");
    console.log(userId, "userId");
    if(!userId) return;
    const { status, ...remainingParams } = queryParams;
    setIsLoader(true);
    const path =
      status === "Test"
        ? `/diagnostic-order/user/${
            userId
          }?status=${filterStatus}&${qs.stringify(remainingParams)}`
        : status === "Pharmacy"
        ? `/pharma-order/user/${
            userId
          }?status=${filterStatus}&${qs.stringify(remainingParams)}`
        : "";
    const { res, err } = await httpRequest<iMyOrdersResponse>({ path });
    if (res) {
      let { documents = [], paginated } = res;
      setAllOrders(documents);
      setPaginated(paginated);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  // Fetch orders when queryParams changes
  useEffect(() => {
    fetchMyOrders();
  }, [queryParams, filterStatus]);

  return (
    <>
      <PageHeader title="Order List" />
      <div
        style={{
          minHeight: `calc(${
            document.getElementById("customer-sidebar-id")?.offsetHeight
          }px - 1rem - ${document.getElementById("headerH")?.offsetHeight}px)`,
        }}
        className="parent-box parent-box-height"
      >
        <div className="flex justify-between items-center">
          <div className="products-tabs-btn">
            {tabList.map((tab) => (
              <button
                disabled={queryParams?.status === tab.name}
                key={tab.name}
                className={`font-size-15px font-Poppins-Medium rounded-pill relative ${
                  queryParams?.status === tab.name && "active"
                }`}
                onClick={() => handleTabClick(tab.name)}
              >
                {tab.name}
                {/* {tab.count > 0 && (
                                    <div className="red-dot-show absolute -top-2 -right-1">
                                    </div>
                                )} */}
                {tab.name === "Test" && allIndicators?.customerOrder > 0 ? (
                  <div className="red-dot-show absolute -top-2 -right-1"></div>
                ) : tab.name === "Pharmacy" &&
                  allIndicators?.customerPharmacyOrder > 0 ? (
                  <div className="red-dot-show absolute -top-2 -right-1"></div>
                ) : null}
              </button>
            ))}
          </div>
          <div className="flex flex-row items-center gap-4">
            <SelectDropdown
              checkboxShow={false}
              isMulti={false}
              options={orderStatus}
              data={orderStatus?.filter((v: any) => v?.value === filterStatus)}
              setData={(newVal) => {
                setFilterstatus(newVal[0]?.value || "");
              }}
              extraClass="filter_status"
              // placeholder={{ selectSomeItems: "Location" }}
            />
            <SearchFilterInput
              key={queryParams?.status}
              inputExtraStyle={{ marginBottom: 0 }}
              onChangeText={handleFilterChange}
            />
          </div>
        </div>
        <hr className="products-tabs-hr-allOrder" />
        {queryParams?.status === "Test" ||
        queryParams?.status === "Pharmacy" ? (
          <CustomerTableWrapper
            isLoading={isLoader}
            thead={[
              "Order ID",
              "Product Name",
              "Total Amount",
              "Date",
              "Status",
              "Actions",
            ]}
            page={paginated?.currentPage || 1}
            total={paginated?.totalPages || 0}
            columnWidth={"14% 25% 16% 17% 14% 14%"}
            onChange={handlePageChange}
          >
            {allOrders?.map((item: IOrderItems, index: number) => (
              <ul
                key={index}
                className={`font-size-16px tbody-light-table tbody-light-table-customer customer-table-wrapper-body-padding-non customer-table-wrapper-body font-Poppins-Regular grid ${
                  !item?.customerNotified ? `font-bold` : ``
                }`}
                style={{ gridTemplateColumns: "14% 25% 16% 17% 14% 14%" }}
              >
                <li className="action-last-colum-non">
                  {getLastSegment(item?.serialOrderId)}
                </li>
                <li>
                  <p className="truncate w-[80%] pr-3">
                    {item?.orderDetails?.map(
                      (v: { productName: string }, index: number) => {
                        return (
                          <span key={v.productName}>
                            {v.productName}
                            {item?.orderDetails?.length > index + 1 ? ", " : ""}
                          </span>
                        );
                      }
                    )}
                  </p>
                </li>
                <li>{CURRENCY} {item?.totalAmount || 0}</li>
                <li>{moment(item?.createdAt).format("DD-MM-YYYY")}</li>
                <li>
                  <span
                    style={{
                      backgroundColor: displayStatus(item?.status)?.bgColor,
                      color: displayStatus(item?.status)?.color,
                    }}
                    className="py-1 px-4 font-size-13px font-Poppins-SemiBold capitalize rounded"
                  >
                    {displayStatus(item?.status)?.text || ""}
                  </span>
                </li>
                <li className="action-last-colum">
                  <Link to={returnURL(item?._id)}>
                    <img src={eyeIcon} alt="" />
                  </Link>
                </li>
              </ul>
            ))}
          </CustomerTableWrapper>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default MyOrders;
